import { ExternalBatteryPackMode, Solar4gSysInfo } from '@EcamModel/model';
import BaseCheckbox from '@components/BaseCheckbox';
import PopUpBase from '@components/PopUpBase';
import { pushError, pushSuccess } from '@components/toast';
import useMedia from '@hooks/useMedia';
import { IPopUp } from '@hooks/usePopUp';
import { Box, Button, FormControlLabel, Radio, RadioGroup, Stack, Tooltip, Typography } from '@mui/material';
import color from '@theme/Colors';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useBackdrop } from 'src/providers/BackdropProvider';
import InformationIcon from './icon/InformationIcon';
import IOSSwitch from '@components/IOSSwitch';

type Props = Omit<IPopUp, 'onConfirm'> & {
    cellularSettings: Partial<Solar4gSysInfo>;
    onSave: (devMac: string, data: Partial<Solar4gSysInfo>) => Promise<void>;
};

export default function PopUpExternalBatteryPackSettings(props: Props) {
    const { open, onClose, onSave, cellularSettings } = props;
    const { isMediumMD, isMobileSM } = useMedia();
    const backdrop = useBackdrop();

    const {
        control,
        reset,
        handleSubmit,
        formState: { isValid, isDirty },
    } = useForm<Partial<Solar4gSysInfo>>({ mode: 'all' });

    const defaultValues = {
        ExternalBatteryPack: cellularSettings?.ExternalBatteryPack,
    };

    const disabled = !(isValid && isDirty);

    useEffect(() => {
        if (!open) return;
        reset(defaultValues);
    }, [open, cellularSettings]);

    const tooltipText = `If External Power is selected:
When the temperature is below 5°C and the internal battery level is below 80%, the heating function will turn on to warm the internal battery—even if it is not charging. This protects the battery and uses external power, so it does not consume the internal battery.

If Internal Battery is selected:
Heating will only turn on when the internal battery is charging and the temperature is below 5°C. If the internal battery is not charging, heating will not activate even in cold weather.
`;

    return (
        <PopUpBase
            open={open}
            title={<Typography variant="h4">External battery setting</Typography>}
            desc={
                <Box minWidth={isMediumMD ? '100%' : 500} bgcolor={color.grey100} paddingX={2} py={1.5}>
                    <Stack direction={'row'} spacing={4}>
                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                            <Typography>External battery pack:</Typography>
                            {!isMobileSM && (
                                <Tooltip
                                    placement="top"
                                    arrow
                                    title={
                                        <Typography
                                            sx={{
                                                whiteSpace: 'pre-line',
                                                mb: 0,
                                                color: color.white,
                                            }}
                                        >
                                            {tooltipText}
                                        </Typography>
                                    }
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                maxWidth: 400,
                                                whiteSpace: 'normal',
                                            },
                                        },
                                    }}
                                >
                                    <InformationIcon />
                                </Tooltip>
                            )}
                        </Stack>

                        <Controller
                            control={control}
                            name="ExternalBatteryPack"
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <RadioGroup
                                            {...field}
                                            value={field.value}
                                            onChange={(e, checked) => field.onChange(Number(checked))}
                                        >
                                            <Stack direction={isMediumMD ? 'column' : 'row'}>
                                                {[
                                                    {
                                                        label: 'External power battery',
                                                        value: ExternalBatteryPackMode.ExternalPowerBattery,
                                                    },
                                                    {
                                                        label: 'Internal battery',
                                                        value: ExternalBatteryPackMode.InternalBattery,
                                                    },
                                                ].map((item) => {
                                                    return (
                                                        <FormControlLabel
                                                            value={Number(item.value)}
                                                            control={<Radio />}
                                                            label={item.label}
                                                        />
                                                    );
                                                })}
                                            </Stack>
                                        </RadioGroup>
                                    }
                                    label={<></>}
                                    sx={{ ml: 0 }}
                                />
                            )}
                        />
                    </Stack>
                </Box>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        variant="cancel"
                        onClick={() => {
                            onClose?.();
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant={'contained'}
                        disabled={disabled}
                        onClick={handleSubmit((data) => {
                            backdrop.setTrue();

                            onSave(cellularSettings.DevMac!, data)
                                .then(() => {
                                    pushSuccess('Saved successfully');
                                })
                                .catch((res) => {
                                    pushError(res);
                                })
                                .finally(() => {
                                    onClose?.();
                                    backdrop.setFalse();
                                });
                        })}
                    >
                        Save
                    </Button>
                </Stack>
            }
            minWidthButton={130}
        />
    );
}
